/**
 * Calendar — vista mensal de chamados agendados.
 */
import { useQuery } from "@tanstack/react-query";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/page-header";
import { JobStatusPill, UrgencyPill } from "@/components/status-pill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { jobsApi, type Job } from "@/lib/api-jobs";
import { cn } from "@/lib/utils";

export function CalendarPage() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  // Busca chamados AGENDADOS no range visível (server-side por scheduled_at),
  // em vez de pegar os 500 mais recentes por criação e filtrar no cliente.
  const list = useQuery({
    queryKey: ["jobs-calendar", format(gridStart, "yyyy-MM-dd"), format(gridEnd, "yyyy-MM-dd")],
    queryFn: () =>
      jobsApi.list({
        scheduled_from: format(gridStart, "yyyy-MM-dd"),
        scheduled_to: format(gridEnd, "yyyy-MM-dd"),
        order_by: "scheduled_at",
        size: 500,
      }),
  });

  const jobsByDay = useMemo(() => {
    const map = new Map<string, Job[]>();
    list.data?.items.forEach((j) => {
      if (!j.scheduled_at) return;
      try {
        const key = format(parseISO(j.scheduled_at), "yyyy-MM-dd");
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(j);
      } catch {
        // ignore
      }
    });
    return map;
  }, [list.data]);

  return (
    <>
      <PageHeader
        title="Calendário"
        description="Chamados agendados no mês."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>
              Hoje
            </Button>
            <div className="flex items-center rounded-lg border border-slate-200 dark:border-white/10">
              <Button variant="ghost" size="icon-sm" onClick={() => setCursor((c) => subMonths(c, 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-3 text-sm font-medium tabular-nums min-w-32 text-center capitalize">
                {format(cursor, "MMMM yyyy", { locale: ptBR })}
              </span>
              <Button variant="ghost" size="icon-sm" onClick={() => setCursor((c) => addMonths(c, 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        }
      />

      <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200/60 dark:border-white/10 bg-slate-50/80 dark:bg-white/[0.03]">
          {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
            <div key={d} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 auto-rows-[minmax(120px,auto)]">
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayJobs = jobsByDay.get(key) ?? [];
            const outOfMonth = !isSameMonth(day, cursor);
            const today = isToday(day);
            return (
              <div
                key={key}
                className={cn(
                  "border-r border-b border-slate-200/40 dark:border-white/5 last:border-r-0 p-1.5",
                  outOfMonth && "bg-slate-50/40 dark:bg-white/[0.01]",
                )}
              >
                <div
                  className={cn(
                    "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium",
                    today && "bg-primary-600 text-white",
                    !today && outOfMonth && "text-muted-foreground",
                    !today && !outOfMonth && "text-foreground",
                  )}
                >
                  {format(day, "d")}
                </div>
                <div className="mt-1 space-y-1">
                  {dayJobs.slice(0, 3).map((j) => (
                    <Link
                      key={j.id}
                      to={`/jobs/${j.id}`}
                      className="block px-1.5 py-0.5 rounded text-[10px] truncate bg-primary-100/60 dark:bg-primary-500/10 text-primary-800 dark:text-primary-200 hover:bg-primary-200/60 transition-colors"
                      title={`${j.code} — ${j.title}`}
                    >
                      <span className="font-mono mr-1 opacity-70">{format(parseISO(j.scheduled_at!), "HH:mm")}</span>
                      {j.title}
                    </Link>
                  ))}
                  {dayJobs.length > 3 && (
                    <button
                      type="button"
                      onClick={() => navigate(`/jobs?scheduled=${key}`)}
                      className="block w-full px-1.5 py-0.5 rounded text-[10px] text-muted-foreground hover:bg-slate-100 dark:hover:bg-white/5 text-left"
                    >
                      +{dayJobs.length - 3} mais
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legenda */}
      <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-3 w-3" /> {list.data?.items.filter((j) => j.scheduled_at).length ?? 0} agendados
        </span>
        <span className="inline-flex items-center gap-2">
          <UrgencyPill urgency="urgent" />
          <UrgencyPill urgency="same_day" />
          <UrgencyPill urgency="normal" />
        </span>
        <span><JobStatusPill status="open" /></span>
        <span><Badge variant="muted">click no dia ou job pra abrir</Badge></span>
      </div>
    </>
  );
}
